// rigVersions — the decisions behind RigInspector's Replace / Versions /
// Delete controls, kept out of the component so they are testable under
// `node --test` (RigInspector.jsx cannot be imported there).
//
// Pure and dependency-free: no React, no fetch, no import.meta.env.

/** Why the rig controls are unavailable when a character has no rig GLB. */
export const NO_RIG_ACTION_REASON =
  'This character has no rig GLB — it renders from a Gaussian-splat pack, so '
  + 'there is nothing to replace or delete.'

/**
 * rigActionAvailability — whether Replace and Delete can act, and why not.
 *
 * Unavailable controls are SHOWN AND DISABLED with the reason inline, never
 * hidden (the rule voiceInputState.js's header sets out and the existing
 * Download button already follows). So this returns a reason string rather
 * than a bare boolean: the caller has to have something to render.
 *
 * Replace and Delete differ on one case and it matters. A splat character has
 * no rig to delete, but it also cannot be GIVEN one through this panel — the
 * renderer is chosen by the character row, not by uploading a GLB — so both
 * are unavailable there. They are also both unavailable mid-flight, because a
 * second click during an upload would race the first.
 */
export function rigActionAvailability({ hasRig, busy = false } = {}) {
  if (busy) {
    return { canReplace: false, canDelete: false, reason: 'A rig operation is already running.' }
  }
  if (!hasRig) {
    return { canReplace: false, canDelete: false, reason: NO_RIG_ACTION_REASON }
  }
  return { canReplace: true, canDelete: true, reason: null }
}

/**
 * replaceWarning — what the user is told BEFORE a replace uploads.
 *
 * Uploading a rig re-runs the server's GLB import, which re-derives
 * material_mapping. It no longer drops every slot→material binding: a binding a
 * person made is carried forward when the mesh keeps its name across the two
 * GLBs (avatar-asset-manager, glb_import.carryOverBindings). A renamed or
 * removed mesh still loses its binding, and so does one pointing at a material
 * the importer itself created, because the re-import replaces those rows.
 *
 * So this states the RULE, not a prediction. The dialog is raised before the
 * file is uploaded and the client has not parsed the GLB, so it cannot know
 * which mesh names the new rig carries — claiming a survivor count here would
 * be a guess. What actually survived is reported afterwards from the server's
 * own count; see carryOverSummary in rigUpload.js.
 *
 * `assignedCount` is how many slots currently carry a material. Zero is worth
 * saying too — "nothing to lose" is exactly the information that makes the
 * confirmation cheap to accept.
 */
export function replaceWarning(assignedCount) {
  const n = Number.isFinite(assignedCount) ? Math.max(0, Math.trunc(assignedCount)) : 0
  const slots = n === 1 ? '1 material slot is' : `${n} material slots are`
  return n === 0
    ? 'Replacing the rig re-derives the material slots from the new GLB. '
      + 'No slots currently have a material, so nothing is lost.'
    : `Replacing the rig re-derives the material slots from the new GLB. `
      + `Assignments on meshes that keep the same name are carried over; any whose `
      + `mesh was renamed or removed will need re-assigning. ${slots} currently `
      + `assigned. The materials themselves are not deleted.`
}

/** What the user must type to confirm a permanent rig delete. */
export function deleteConfirmationPhrase(slug) {
  return typeof slug === 'string' && slug.trim() !== '' ? slug.trim() : 'delete'
}

/**
 * isDeleteConfirmed — a typed-slug match, not a bare OK.
 *
 * Deleting a rig destroys every version's bytes with no undo and no safety
 * checkpoint (the server takes none, deliberately — the bytes a checkpoint
 * would reference are the ones being removed). A misclick must not be able to
 * do that, so the user types the character's own slug.
 *
 * Trimmed and case-insensitive: the point is deliberate intent, not typing
 * accuracy, and a slug is lowercase anyway.
 */
export function isDeleteConfirmed(typed, slug) {
  if (typeof typed !== 'string') return false
  return typed.trim().toLowerCase() === deleteConfirmationPhrase(slug).toLowerCase()
}

/**
 * formatBytes — size for a version row. Binary units, one decimal above KB.
 * A rig is 10-60 MB, so MB is the unit that actually reads.
 */
export function formatBytes(bytes) {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * describeRigVersion — one version row's label.
 *
 * Uploader falls back to the raw sub, then to nothing — never to "unknown",
 * which reads as a system failure when the truth is simply that rows written
 * before provenance existed carry no email.
 */
export function describeRigVersion(row, { currentUrl = null } = {}) {
  const version = Number(row?.version)
  const size = formatBytes(row?.byteSize ?? row?.byte_size)
  const when = row?.createdAt ?? row?.created_at
  const who = row?.createdByEmail ?? row?.created_by_email ?? ''
  const parts = [`v${Number.isFinite(version) ? version : '?'}`]
  if (size !== '—') parts.push(size)
  if (when) parts.push(formatVersionDate(when))
  if (who) parts.push(who)
  return {
    label: parts.join(' · '),
    isCurrent: !!currentUrl && (row?.fileUrl ?? row?.file_url) === currentUrl,
    note: typeof row?.note === 'string' ? row.note : '',
  }
}

function formatVersionDate(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/**
 * rigVersionRows — the list the panel renders, newest first, current marked.
 *
 * An EMPTY history is a real state, not a failure: every character whose rig
 * predates versioning has one, and the server returns [] for it. The caller
 * renders `emptyReason` rather than a blank panel or an error.
 *
 * Sorting here rather than trusting the server: the panel also has to be
 * correct against an optimistic local insert after an upload, which arrives
 * out of order.
 */
export function rigVersionRows(rows, { currentUrl = null } = {}) {
  const list = Array.isArray(rows) ? rows.slice() : []
  list.sort((a, b) => Number(b?.version ?? 0) - Number(a?.version ?? 0))
  const described = list.map(r => ({ ...describeRigVersion(r, { currentUrl }), row: r }))
  return {
    rows: described,
    // Restoring the version already loaded is a no-op that looks like a bug
    // when it "does nothing", so the current row is not offered the action.
    restorable: described.filter(d => !d.isCurrent),
    emptyReason: described.length === 0
      ? 'No earlier versions — this rig was uploaded before version history existed.'
      : null,
  }
}
